import { MapCloud, MapLink, layoutCloudColumn } from "./mapModel";

/**
 * The site diagram: the topology drawn without geography, for a scope whose
 * sites have no location on file. Sites stack in one column, a DCI is an arc on
 * their left, the clouds sit in a column on their right — the map's vocabulary,
 * so a tenant reads the same picture with or without GPS. Pure, so the layout
 * rules are tested apart from the SVG.
 */

export const DIAGRAM = {
  cardHeight: 72,
  rowGap: 14,
  padding: 24,
  /** Kept under the drawing for the legend. */
  legendHeight: 44,
  minCardWidth: 200,
  maxCardWidth: 320,
  /** Horizontal room the cloud lines take between the cards and the clouds. */
  lineSpan: 150,
  cloudColumn: 120,
  cloudGap: 72,
  /** How far left the shortest arc reaches; each extra row it spans adds `arcStep`. */
  arcDepth: 22,
  arcStep: 16,
  captionHeight: 18,
  minHeight: 260,
} as const;

/** A DCI caption's width in px: 10 px bold text plus its padding. */
export function captionWidth(label: string): number {
  return label.length * 6.2 + 16;
}

/**
 * Top-to-bottom order of the sites: the sites joined by DCIs next to each
 * other, so arcs stay short and do not cross. Each group of linked sites starts
 * from its busiest site and is walked breadth-first; bigger groups go first,
 * lone sites last, ties by id so the order holds between renders.
 */
export function orderSites(siteIds: string[], links: Pick<MapLink, "from" | "to">[]): string[] {
  const neighbours = new Map<string, Set<string>>(siteIds.map((id) => [id, new Set<string>()]));
  for (const link of links) {
    neighbours.get(link.from)?.add(link.to);
    neighbours.get(link.to)?.add(link.from);
  }
  const degree = (id: string) => neighbours.get(id)?.size ?? 0;
  const byBusiest = [...siteIds].sort((a, b) => degree(b) - degree(a) || a.localeCompare(b));

  const seen = new Set<string>();
  const groups: string[][] = [];
  for (const start of byBusiest) {
    if (seen.has(start)) continue;
    const group: string[] = [];
    const queue = [start];
    seen.add(start);
    while (queue.length > 0) {
      const current = queue.shift() as string;
      group.push(current);
      const next = [...(neighbours.get(current) ?? [])].filter((id) => !seen.has(id)).sort();
      next.forEach((id) => seen.add(id));
      queue.push(...next);
    }
    groups.push(group);
  }
  return groups.sort((a, b) => b.length - a.length || a[0].localeCompare(b[0])).flat();
}

export interface DiagramArc {
  key: string;
  label: string;
  /** Centre heights of the two cards it joins, top first. */
  top: number;
  bottom: number;
  /** How far left of the cards the arc's apex reaches. */
  depth: number;
  caption: { x: number; y: number };
}

export interface DiagramLayout {
  height: number;
  cardLeft: number;
  cardWidth: number;
  /** Centre height of each site's card. */
  siteY: Record<string, number>;
  arcs: DiagramArc[];
  cloudX: number;
  cloudY: Record<string, number>;
}

/**
 * Where everything goes, in CSS px for a drawing `width` wide. The column of
 * cards and the clouds are centred as one block, so two sites and no cloud do
 * not hug the left edge. Arcs that span more rows reach further out, which
 * nests them instead of crossing them; a caption that would sit on another one
 * steps down.
 */
export function layoutDiagram(order: string[], links: MapLink[], clouds: MapCloud[], width: number): DiagramLayout {
  const row = new Map(order.map((id, index) => [id, index]));
  const spans = links
    .map((link) => {
      const a = row.get(link.from);
      const b = row.get(link.to);
      return a === undefined || b === undefined ? null : { link, first: Math.min(a, b), last: Math.max(a, b) };
    })
    .filter((span): span is { link: MapLink; first: number; last: number } => span !== null);
  const depthOf = (first: number, last: number) => DIAGRAM.arcDepth + DIAGRAM.arcStep * (last - first - 1);
  const gutter = spans.reduce(
    (widest, { link, first, last }) => Math.max(widest, depthOf(first, last) + captionWidth(link.label) / 2 + 8),
    0
  );

  const cloudsWidth = clouds.length > 0 ? DIAGRAM.lineSpan + DIAGRAM.cloudColumn : 0;
  const available = width - 2 * DIAGRAM.padding - gutter - cloudsWidth;
  const cardWidth = Math.min(DIAGRAM.maxCardWidth, Math.max(DIAGRAM.minCardWidth, available));
  const blockWidth = gutter + cardWidth + cloudsWidth;
  const left = Math.max(DIAGRAM.padding, (width - blockWidth) / 2);
  const cardLeft = left + gutter;

  const stack = order.length * DIAGRAM.cardHeight + Math.max(order.length - 1, 0) * DIAGRAM.rowGap;
  const cloudStack = clouds.length > 0 ? (clouds.length - 1) * DIAGRAM.cloudGap + 48 : 0;
  const content = Math.max(stack, cloudStack);
  const height = Math.max(DIAGRAM.minHeight, content + 2 * DIAGRAM.padding + DIAGRAM.legendHeight);
  // Centred in the room above the legend.
  const contentTop = DIAGRAM.padding + (height - DIAGRAM.legendHeight - 2 * DIAGRAM.padding - content) / 2;
  const stackTop = contentTop + (content - stack) / 2;
  const siteY = Object.fromEntries(
    order.map((id, index) => [id, stackTop + index * (DIAGRAM.cardHeight + DIAGRAM.rowGap) + DIAGRAM.cardHeight / 2])
  );

  const placedCaptions: { x: number; y: number; half: number }[] = [];
  const arcs = spans
    .sort((a, b) => a.last - a.first - (b.last - b.first))
    .map(({ link, first, last }) => {
      const depth = depthOf(first, last);
      const top = siteY[order[first]];
      const bottom = siteY[order[last]];
      const x = cardLeft - depth;
      const half = captionWidth(link.label) / 2;
      let y = (top + bottom) / 2;
      const collides = (at: number) =>
        placedCaptions.some(
          (other) => Math.abs(other.y - at) < DIAGRAM.captionHeight + 4 && Math.abs(other.x - x) < other.half + half
        );
      while (collides(y)) y += DIAGRAM.captionHeight + 4;
      placedCaptions.push({ x, y, half });
      return { key: link.key, label: link.label, top, bottom, depth, caption: { x, y } };
    });

  const cloudY = layoutCloudColumn(clouds, (node) => siteY[node] ?? height / 2, {
    top: contentTop + 24,
    bottom: contentTop + content - 24,
    minGap: DIAGRAM.cloudGap,
  });

  return {
    height,
    cardLeft,
    cardWidth,
    siteY,
    arcs,
    cloudX: cardLeft + cardWidth + DIAGRAM.lineSpan + DIAGRAM.cloudColumn / 2,
    cloudY,
  };
}
